<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';

const emptyForm = () => ({
  chat_type: 'personal',
  identifier: '',
  label: '',
  reason: '',
});

export default {
  components: {
    NextButton,
    SpinnerLoader,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      exclusions: [],
      candidates: [],
      form: emptyForm(),
      editingId: null,
      isLoading: false,
      isLoadingCandidates: false,
      isSaving: false,
      deletingId: null,
      selectedCandidateId: '',
    };
  },
  computed: {
    isEditing() {
      return !!this.editingId;
    },
    identifierHelpText() {
      if (this.inbox.channel_type === 'Channel::WhatsappWeb') {
        return this.form.chat_type === 'group'
          ? this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.WHATSAPP_GROUP_HELP')
          : this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.WHATSAPP_PERSONAL_HELP');
      }
      return this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.TELEGRAM_HELP');
    },
  },
  mounted() {
    this.fetchExclusions();
    this.fetchCandidates();
  },
  methods: {
    async fetchExclusions() {
      try {
        this.isLoading = true;
        const response = await InboxesAPI.getChatExclusions(this.inbox.id);
        this.exclusions = response.data;
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.LOAD_ERROR')
        );
      } finally {
        this.isLoading = false;
      }
    },
    async fetchCandidates() {
      try {
        this.isLoadingCandidates = true;
        const response = await InboxesAPI.getChatExclusionCandidates(
          this.inbox.id
        );
        this.candidates = response.data;
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.CANDIDATES_LOAD_ERROR')
        );
      } finally {
        this.isLoadingCandidates = false;
      }
    },
    selectCandidate() {
      const candidate = this.candidates.find(
        item => String(item.id) === String(this.selectedCandidateId)
      );
      if (!candidate) return;

      this.editingId = null;
      this.form = {
        chat_type: candidate.chat_type,
        identifier: candidate.identifier,
        label: candidate.label || '',
        reason: '',
      };
    },
    async saveExclusion() {
      if (!this.form.identifier.trim()) {
        useAlert(this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.IDENTIFIER_REQUIRED'));
        return;
      }

      try {
        this.isSaving = true;
        const payload = {
          chat_type: this.form.chat_type,
          identifier: this.form.identifier.trim(),
          label: this.form.label.trim(),
          reason: this.form.reason.trim(),
        };
        if (this.editingId) {
          await InboxesAPI.updateChatExclusion(
            this.inbox.id,
            this.editingId,
            payload
          );
        } else {
          await InboxesAPI.createChatExclusion(this.inbox.id, payload);
        }
        this.resetForm();
        await this.fetchExclusions();
        await this.fetchCandidates();
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.SAVE_ERROR')
        );
      } finally {
        this.isSaving = false;
      }
    },
    editExclusion(exclusion) {
      this.editingId = exclusion.id;
      this.form = {
        chat_type: exclusion.chat_type,
        identifier: exclusion.identifier,
        label: exclusion.label || '',
        reason: exclusion.reason || '',
      };
    },
    resetForm() {
      this.editingId = null;
      this.selectedCandidateId = '';
      this.form = emptyForm();
    },
    async deleteExclusion(exclusion) {
      try {
        this.deletingId = exclusion.id;
        await InboxesAPI.deleteChatExclusion(this.inbox.id, exclusion.id);
        await this.fetchExclusions();
        await this.fetchCandidates();
        if (this.editingId === exclusion.id) this.resetForm();
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.CHAT_EXCLUSIONS.DELETE_ERROR')
        );
      } finally {
        this.deletingId = null;
      }
    },
    formattedDate(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString();
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h3 class="text-heading-3 text-n-slate-12">
        {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-0">
        {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.DESCRIPTION') }}
      </p>
    </div>

    <label>
      {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.CANDIDATE') }}
      <select
        v-model="selectedCandidateId"
        :disabled="isLoadingCandidates"
        @change="selectCandidate"
      >
        <option value="">
          {{
            isLoadingCandidates
              ? $t('INBOX_MGMT.CHAT_EXCLUSIONS.CANDIDATES_LOADING')
              : $t('INBOX_MGMT.CHAT_EXCLUSIONS.CANDIDATE_PLACEHOLDER')
          }}
        </option>
        <option
          v-for="candidate in candidates"
          :key="candidate.id"
          :value="candidate.id"
          :disabled="candidate.excluded"
        >
          {{
            `${candidate.label} · ${candidate.chat_type} · ${candidate.identifier}${
              candidate.excluded
                ? ` · ${$t('INBOX_MGMT.CHAT_EXCLUSIONS.ALREADY_EXCLUDED')}`
                : ''
            }`
          }}
        </option>
      </select>
      <span class="message">
        {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.CANDIDATE_HELP') }}
      </span>
    </label>

    <div class="grid grid-cols-1 md:grid-cols-[18rem_1fr] gap-4">
      <label>
        {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.TYPE') }}
        <select v-model="form.chat_type">
          <option value="personal">
            {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.PERSONAL') }}
          </option>
          <option value="group">
            {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.GROUP') }}
          </option>
        </select>
      </label>

      <label>
        {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.IDENTIFIER') }}
        <input
          v-model="form.identifier"
          type="text"
          :placeholder="$t('INBOX_MGMT.CHAT_EXCLUSIONS.IDENTIFIER_PLACEHOLDER')"
        />
        <span class="message">{{ identifierHelpText }}</span>
      </label>

      <label>
        {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.LABEL') }}
        <input
          v-model="form.label"
          type="text"
          :placeholder="$t('INBOX_MGMT.CHAT_EXCLUSIONS.LABEL_PLACEHOLDER')"
        />
      </label>

      <label>
        {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.REASON') }}
        <input
          v-model="form.reason"
          type="text"
          :placeholder="$t('INBOX_MGMT.CHAT_EXCLUSIONS.REASON_PLACEHOLDER')"
        />
      </label>
    </div>

    <div class="flex flex-wrap gap-3">
      <NextButton
        blue
        solid
        :is-loading="isSaving"
        :label="
          isEditing
            ? $t('INBOX_MGMT.CHAT_EXCLUSIONS.UPDATE')
            : $t('INBOX_MGMT.CHAT_EXCLUSIONS.ADD')
        "
        @click="saveExclusion"
      />
      <NextButton
        v-if="isEditing"
        slate
        faded
        :label="$t('INBOX_MGMT.CHAT_EXCLUSIONS.CANCEL')"
        @click="resetForm"
      />
    </div>

    <div v-if="isLoading" class="flex h-32 items-center justify-center">
      <SpinnerLoader :size="24" class="text-n-blue-9" />
    </div>

    <div v-else class="overflow-x-auto border border-n-weak rounded-lg">
      <table class="min-w-full text-sm">
        <thead class="bg-n-surface-2 text-n-slate-11">
          <tr>
            <th class="text-left p-3">
              {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.LABEL') }}
            </th>
            <th class="text-left p-3">
              {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.TYPE') }}
            </th>
            <th class="text-left p-3">
              {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.IDENTIFIER') }}
            </th>
            <th class="text-left p-3">
              {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.LAST_MATCHED') }}
            </th>
            <th class="text-right p-3">
              {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!exclusions.length">
            <td colspan="5" class="p-4 text-center text-n-slate-11">
              {{ $t('INBOX_MGMT.CHAT_EXCLUSIONS.EMPTY') }}
            </td>
          </tr>
          <tr
            v-for="exclusion in exclusions"
            :key="exclusion.id"
            class="border-t border-n-weak"
          >
            <td class="p-3">
              <div class="font-medium text-n-slate-12">
                {{ exclusion.label || '-' }}
              </div>
              <div v-if="exclusion.reason" class="text-xs text-n-slate-11">
                {{ exclusion.reason }}
              </div>
            </td>
            <td class="p-3 capitalize">{{ exclusion.chat_type }}</td>
            <td class="p-3">
              <div class="font-mono text-xs">{{ exclusion.identifier }}</div>
              <div class="font-mono text-xs text-n-slate-10">
                {{ exclusion.normalized_identifier }}
              </div>
            </td>
            <td class="p-3">{{ formattedDate(exclusion.last_matched_at) }}</td>
            <td class="p-3">
              <div class="flex justify-end gap-2">
                <NextButton
                  slate
                  faded
                  size="small"
                  :label="$t('INBOX_MGMT.CHAT_EXCLUSIONS.EDIT')"
                  @click="editExclusion(exclusion)"
                />
                <NextButton
                  ruby
                  faded
                  size="small"
                  :is-loading="deletingId === exclusion.id"
                  :label="$t('INBOX_MGMT.CHAT_EXCLUSIONS.DELETE')"
                  @click="deleteExclusion(exclusion)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
